import { useMemo } from 'react';
import { useGetRestaurantSettingsQuery } from '../services/restaurantsApi';
import dayjs from 'dayjs';
import { startOfWeekInTz, DEFAULT_TIMEZONE } from '../utils/dateHelpers';

/**
 * Hook to get the restaurant's configured timezone and provide
 * utility functions for timezone-aware date operations.
 */
export const useRestaurantTimeZone = () => {
  const { data: settings, isLoading } = useGetRestaurantSettingsQuery();

  const timeZone = useMemo(() => {
    return settings?.timeZone || DEFAULT_TIMEZONE;
  }, [settings?.timeZone]);

  /**
   * Get the current date in the restaurant's timezone
   * @param format - dayjs format string (default: 'YYYY-MM-DD')
   */
  const getLocalDate = (format: string = 'YYYY-MM-DD') => {
    return dayjs().tz(timeZone).format(format);
  };

  /**
   * Convert a UTC date to the restaurant's timezone
   * @param utcDate - UTC date string or Date object
   */
  const toLocalTime = (utcDate: string | Date) => {
    return dayjs(utcDate).tz(timeZone);
  };

  /**
   * Get the start of the current day in the restaurant's timezone
   */
  const startOfDay = () => dayjs().tz(timeZone).startOf('day');

  /**
   * Get the end of the current day in the restaurant's timezone
   */
  const endOfDay = () => dayjs().tz(timeZone).endOf('day');

  /**
   * Get the start of the current week (Monday) in the restaurant's timezone.
   * Delegates to `startOfWeekInTz` in dateHelpers — see that function for the
   * Sunday-handling rationale and tests.
   */
  const startOfWeek = () => startOfWeekInTz(timeZone);

  /**
   * Get the start of the current month in the restaurant's timezone
   */
  const startOfMonth = () => dayjs().tz(timeZone).startOf('month');

  /**
   * Format a date in the restaurant's timezone
   * @param date - Date to format
   * @param format - dayjs format string
   */
  const formatInTimeZone = (date: string | Date, format: string = 'DD/MM/YYYY HH:mm') => {
    return dayjs(date).tz(timeZone).format(format);
  };

  return {
    timeZone,
    isLoading,
    getLocalDate,
    toLocalTime,
    startOfDay,
    endOfDay,
    startOfWeek,
    startOfMonth,
    formatInTimeZone,
  };
};

export default useRestaurantTimeZone;
